/**
 * Portal Manta · MatrizTiposObra
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, fmtNum, sigKey, AmpliBadge, OBRAS_ROD } from '../../globals.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

function MatrizTiposObra({lote}){
  const sig = sigKey(lote);
  const od = OBRAS_ROD[sig]||{};
  const sistema = od.sistema||[];
  const extLote = sistema.reduce((s,r)=>s+(r.ext||0),0)||1;
  const tipos = [
    {k:'duplicacoes',l:'Duplicação',cor:'#1d4ed8',field:'km',unid:'km'},
    {k:'contornos',l:'Contornos',cor:'#7c3aed',field:'km',unid:'km'},
    {k:'faixas',l:'Faixa Adic.',cor:'#ea580c',field:'km',unid:'km'},
    {k:'marginais',l:'Marginais',cor:'#16a34a',field:'km',unid:'km'},
    {k:'ciclovias',l:'Ciclovias',cor:'#db2777',field:'km',unid:'km'},
    {k:'oaes',l:'OAEs',cor:'#7c3aed',field:'n',unid:'un'},
    {k:'passarelas',l:'Passarelas',cor:'#0891b2',field:'n',unid:'un'},
    {k:'diamantes',l:'Diamantes',cor:'#f59e0b',field:'n',unid:'un'},
    {k:'trombetas',l:'Trombetas',cor:'#84cc16',field:'n',unid:'un'},
    {k:'parclos',l:'Parclos',cor:'#10b981',field:'n',unid:'un'},
    {k:'rotatorias',l:'Rotatórias',cor:'#94a3b8',field:'n',unid:'un'},
  ];
  // Matriz: rodovia × tipo de obra
  const cellVal = (rod,t)=>(od[t.k]||[]).filter(x=>x.rod===rod).reduce((s,x)=>s+(x[t.field]||(t.field==='n'?1:0)),0);
  // Max por coluna (tipo) para normalização
  const maxPorTipo = {};
  tipos.forEach(t=>{
    maxPorTipo[t.k] = Math.max(...sistema.map(r=>cellVal(r.rod,t)),1);
  });
  const corCelula = (cor, intensity)=>{
    if(intensity<=0) return '#fff';
    const hex = cor.replace('#','');
    const r=parseInt(hex.slice(0,2),16),g=parseInt(hex.slice(2,4),16),b=parseInt(hex.slice(4,6),16);
    const t = Math.min(0.85, intensity*0.85);
    return `rgb(${Math.round(255+(r-255)*t)},${Math.round(255+(g-255)*t)},${Math.round(255+(b-255)*t)})`;
  };
  // Quais tipos têm pelo menos 1 ocorrência (filtra colunas vazias)
  const tiposAtivos = tipos.filter(t=>sistema.some(r=>cellVal(r.rod,t)>0));
  return(<div style={S.page}>
    <div style={S.h1}>Matriz Tipos de Obra × Rodovias <AmpliBadge/></div>
    <div style={S.sub}>
      Quantidades físicas de cada tipo de obra de ampliação por rodovia · {sistema.length} rodovias × {tiposAtivos.length} tipos
    </div>
    <div style={{...S.card,overflow:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
        <thead style={S.thRow}>
          <tr style={{borderBottom:'2px solid '+V.b1}}>
            <th style={{...THl,minWidth:140,position:'sticky',left:0,background:V.g0,zIndex:2}}>Rodovia</th>
            <th style={TH}>Ext (km)</th>
            {tiposAtivos.map(t=>(
              <th key={t.k} style={{...TH,minWidth:90,background:t.cor+'22',color:t.cor}}>
                {t.l}<br/><span style={{fontSize:8.5,fontWeight:500,color:V.gr}}>({t.unid})</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sistema.map(r=>(<tr key={r.rod} style={S.trLine}>
            <td style={{...TDl,fontWeight:700,color:V.br,position:'sticky',left:0,background:'#fff',borderRight:'1px solid '+V.b1}}>{r.rod}</td>
            <td style={TD}>{fmtNum(r.ext,1)}</td>
            {tiposAtivos.map(t=>{
              const v = cellVal(r.rod,t);
              const intensity = maxPorTipo[t.k]?v/maxPorTipo[t.k]:0;
              const bg = corCelula(t.cor, intensity);
              return(<td key={t.k} style={{
                ...TD,padding:'8px 6px',background:bg,
                textAlign:'center',fontFamily:'monospace',
                color:intensity>0.5?'#fff':V.br,fontWeight:intensity>0.3?700:500
              }}>{v?(t.field==='km'?fmtNum(v,1):v):'—'}</td>);
            })}
          </tr>))}
          <tr style={S.tfootRow}>
            <td colSpan={2} style={{padding:'8px 10px',fontSize:11,fontWeight:700,position:'sticky',left:0,background:V.g0,borderRight:'1px solid '+V.b1}}>TOTAL TIPO</td>
            {tiposAtivos.map(t=>{
              const tot = sistema.reduce((s,r)=>s+cellVal(r.rod,t),0);
              return(<td key={t.k} style={{padding:'8px 6px',fontSize:10,fontWeight:700,textAlign:'center',background:t.cor+'33',color:V.br}}>
                {t.field==='km'?fmtNum(tot,1):tot} <span style={{fontSize:8.5,color:V.gr,fontWeight:500}}>{t.unid}</span>
              </td>);
            })}
          </tr>
        </tbody>
      </table>
    </div>
    <div style={{...S.cardP,marginTop:10,fontSize:10,color:V.gr}}>
      <b>Como ler:</b> intensidade da cor = magnitude relativa dentro do tipo (coluna). Útil para identificar concentração — ex.: a rodovia com mais duplicação, mais OAEs, mais marginais. <b>Convenção L4:</b> os contornos novos (Cont. Norte Londrina e Cont. Itaúna do Sul) são listados como rodovias próprias no OBRAS_ROD, mas o PER engloba na BR-376/PR. Por isso Σ rodovias L4 (663 km) é maior que a extensão do lote (628 km) por ~35 km.
    </div>
  </div>);
}

export { MatrizTiposObra };
export default MatrizTiposObra;
